/**
 * Where the reader chooses the theme: light, dark, or the system's setting.
 *
 * - `ThemeMenu`, in the header on larger screens: a button showing the current choice that opens
 *   a menu of the three. Arrow keys, Home and End move through it, Enter or Space chooses, and
 *   Escape closes it.
 * - `ThemeSwitch`, in the menu on small screens: the three side by side, as radio buttons.
 */

import { Check, Monitor, Moon, Sun } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";

import { setThemeChoice, type ThemeChoice, useThemeChoice } from "../theme.js";
import { focusRing } from "./ui.js";

const CHOICES = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
] as const satisfies readonly { value: ThemeChoice; label: string; icon: typeof Sun }[];

export function ThemeMenu() {
  const choice = useThemeChoice();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  const current = CHOICES.find((option) => option.value === choice) ?? CHOICES[2];

  // The open menu takes the focus on the current choice, and closes on a press elsewhere.
  useEffect(() => {
    if (!open) {
      return;
    }
    items.current[CHOICES.findIndex((option) => option.value === choice)]?.focus();
    function onPointerDown(event: PointerEvent) {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, choice]);

  function close() {
    setOpen(false);
    button.current?.focus();
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const focused = items.current.indexOf(document.activeElement as HTMLButtonElement);
    const last = CHOICES.length - 1;
    const moves: Record<string, number> = {
      ArrowDown: focused >= last ? 0 : focused + 1,
      ArrowUp: focused <= 0 ? last : focused - 1,
      Home: 0,
      End: last,
    };
    const next = moves[event.key];
    if (next !== undefined) {
      event.preventDefault();
      items.current[next]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  }

  return (
    <div ref={root} className="relative">
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Theme: ${current.label}`}
        onClick={() => setOpen((value) => !value)}
        className={`grid size-9 place-items-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-900 aria-expanded:bg-zinc-900/[0.05] aria-expanded:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/[0.07] dark:hover:text-white dark:aria-expanded:bg-white/[0.07] dark:aria-expanded:text-white ${focusRing}`}
      >
        <current.icon aria-hidden className="size-[1.125rem]" />
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Theme"
          tabIndex={-1}
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 z-40 mt-2 w-44 rounded-xl bg-white p-1 shadow-xl ring-1 shadow-zinc-900/10 ring-zinc-900/10 dark:bg-ink-850 dark:shadow-black/50 dark:ring-white/10"
        >
          {CHOICES.map((option, index) => {
            const checked = option.value === choice;
            return (
              <button
                key={option.value}
                ref={(element) => {
                  items.current[index] = element;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={checked}
                tabIndex={-1}
                onClick={() => {
                  setThemeChoice(option.value);
                  close();
                }}
                className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors focus:outline-none ${
                  checked
                    ? "font-medium text-zinc-950 dark:text-white"
                    : "text-zinc-600 dark:text-zinc-300"
                } hover:bg-zinc-900/[0.05] focus-visible:bg-zinc-900/[0.05] dark:hover:bg-white/[0.07] dark:focus-visible:bg-white/[0.07]`}
              >
                <option.icon aria-hidden className="size-4 text-zinc-400 dark:text-zinc-500" />
                {option.label}
                <Check
                  aria-hidden
                  className={`ml-auto size-4 text-brand-600 dark:text-brand-400 ${checked ? "" : "invisible"}`}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** The three choices side by side, as radio buttons, for the menu on small screens. */
export function ThemeSwitch() {
  const choice = useThemeChoice();
  const name = useId();
  return (
    <fieldset className="flex items-center justify-between gap-3">
      <legend className="sr-only">Theme</legend>
      <span aria-hidden className="text-sm font-medium text-zinc-500 dark:text-zinc-400">
        Theme
      </span>
      <div className="inline-flex rounded-xl bg-zinc-900/[0.04] p-1 ring-1 ring-zinc-900/[0.06] ring-inset dark:bg-white/[0.04] dark:ring-white/10">
        {CHOICES.map((option) => (
          <label key={option.value} className="relative">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === choice}
              onChange={() => setThemeChoice(option.value)}
              className="peer sr-only"
            />
            <span className="flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-zinc-500 transition peer-checked:bg-white peer-checked:text-zinc-950 peer-checked:shadow-[0_1px_2px_rgb(24_24_27/0.08),0_0_0_1px_rgb(24_24_27/0.06)] peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-500 dark:text-zinc-400 dark:peer-checked:bg-white/10 dark:peer-checked:text-white dark:peer-checked:shadow-none">
              <option.icon aria-hidden className="size-3.5" />
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
